import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Divider,
  Pagination,
  SelectChangeEvent,
  Stack,
} from "@mui/material";
import { useLanguage } from "../../Language";
import PrintIcon from "@mui/icons-material/Print";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { HostSystemItemSortType } from "@tlc/ls2pac/dto/HostSystemItemSortType";
import { HostSystemItemView } from "@tlc/ls2pac/dto/HostSystemItemView";

import AccountPrintLoansDialog, {
  getDateString,
} from "./AccountPrintLoansDialog";
import { LoansSortControl } from "./HostSystemItemSortControl";

import { LSResultItemSkeleton } from "../search/ls/LSResultItem/LSResultItemSkeleton";
import { LoanItem } from "./LoanItem";
import { SharedViewHeader } from "./SharedViewHeader";
import { useDialog } from "@tlc/ui/dialog/useDialog";
import { UpdateDigitalLoansButton } from "./UpdateDigitalLoansButton";
import { useLoansSelection } from "./LoansSelectionProvider";

export interface LoansViewData {
  page: number;
  pageSize: number;
  sort: HostSystemItemSortType;
  totalLoans: number;
  totalLoansOverdue: number;
  loans: HostSystemItemView[];
}

export interface SharedLoansViewProps extends LoansViewData {
  displayedLoan: HostSystemItemView[];
  requiresDateRange: boolean;
  toolbarActions?: React.ReactNode;
  isLoading?: boolean;
  fetchPrintData?: () => Promise<HostSystemItemView[]>;
  onRenewSelected?: (selectedResourceIds: readonly number[]) => void;
  onReturnEResource?: (loan: HostSystemItemView) => void;
  isStudent: boolean;
  patronId: string;
  digitalInformationLastUpdated?: number;
}

export const SharedLoansView: React.FC<SharedLoansViewProps> = ({
  page,
  pageSize,
  sort,
  totalLoans,
  totalLoansOverdue,
  displayedLoan,
  toolbarActions,
  isLoading,
  fetchPrintData,
  onRenewSelected,
  onReturnEResource,
  isStudent,
  patronId,
  digitalInformationLastUpdated,
}) => {
  const { translate } = useLanguage();
  const {
    selected,
    selectedLength,
    isAllIndeterminate,
    isAllSelected,
    handleSelectAllChange,
  } = useLoansSelection();

  const { scrollToTop } = useOutletContext<{ scrollToTop: () => void }>();

  const [searchParams, setSearchParams] = useSearchParams();

  const [isPrintLoading, setIsPrintLoading] = React.useState(false);
  const [loanPrintData, setLoanPrintData] = React.useState<
    HostSystemItemView[]
  >([]);

  const {
    open: printDialogOpen,
    closeDialog: closePrintDialog,
    openDialog: openPrintDialog,
  } = useDialog();

  const pages = totalLoans ? Math.ceil(totalLoans / pageSize) : 0;
  const showSkeletons = isLoading;

  const handleSortChange = (event: SelectChangeEvent) => {
    const newSort = event.target.value as HostSystemItemSortType;
    searchParams.set("page", "1");
    searchParams.set("sort", newSort);
    setSearchParams(searchParams);
  };

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    newPage: number,
  ) => {
    searchParams.set("page", newPage.toString());
    setSearchParams(searchParams);
    scrollToTop();
  };

  const handlePrintClick = async () => {
    if (isStudent || !fetchPrintData) return;
    setIsPrintLoading(true);
    try {
      const data = await fetchPrintData();
      data.forEach((loan) => {
        if (loan.outDate) loan.outDateString = getDateString(loan.outDate);
        if (loan.dueDate) loan.dueDateString = getDateString(loan.dueDate);
      });
      setLoanPrintData(data);
      openPrintDialog();
    } catch (e) {
      console.error("Error fetching loans for printing", e);
    } finally {
      setIsPrintLoading(false);
    }
  };

  const updateLoansButton = React.useMemo(() => {
    return isStudent ? (
      <UpdateDigitalLoansButton patronId={patronId} />
    ) : (
      <UpdateDigitalLoansButton
        patronId={patronId}
        lastUpdated={digitalInformationLastUpdated}
      />
    );
  }, [patronId, digitalInformationLastUpdated]);

  return (
    <>
      {displayedLoan.length === 0 && (
        <Alert severity="info" action={updateLoansButton} sx={{ ml: 1 }}>
          {isStudent
            ? translate("studentNoLoansMessage")
            : translate("noLoansMessage")}
        </Alert>
      )}

      {displayedLoan.length > 0 && (
        <>
          <SharedViewHeader
            labelKey="loans"
            count={totalLoans}
            secondaryLabelKey="overdue"
            secondaryCount={totalLoansOverdue}
            showSelectAll={isStudent ? false : true}
            selectedLength={selectedLength}
            isAllIndeterminate={isAllIndeterminate}
            isAllSelected={isAllSelected}
            handleSelectAllChange={handleSelectAllChange}
            headerActions={
              <>
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  {toolbarActions}
                  {fetchPrintData && (
                    <Button
                      onClick={handlePrintClick}
                      startIcon={<PrintIcon aria-hidden="true" />}
                      disabled={showSkeletons}
                    >
                      {`${translate("print")} ${translate("loans")}`}
                    </Button>
                  )}
                </Box>
              </>
            }
            selectedActions={
              <>
                {selectedLength > 0 && !isStudent && (
                  <Button
                    onClick={() => onRenewSelected(selected as number[])}
                    disabled={isLoading}
                  >
                    {isLoading
                      ? ""
                      : `${translate(
                          "renewSelected",
                        )} ${selectedLength} ${translate("selected")}`}
                  </Button>
                )}
              </>
            }
            sortControl={
              <LoansSortControl
                value={sort}
                onChange={handleSortChange}
                sx={{
                  order: { xs: 0, sm: 1 },
                  marginLeft: { sm: "auto" },
                }}
              />
            }
          />

          {showSkeletons ? (
            <>
              {Array.from({ length: pageSize }).map((_, index) => (
                <LSResultItemSkeleton key={index} hideActions hideCheckbox />
              ))}
            </>
          ) : (
            <Box sx={{ pl: 0 }}>
              <>
                <Box>
                  {displayedLoan.map((loan) => {
                    return (
                      <React.Fragment key={loan.id}>
                        <Divider />

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1,
                          }}
                        >
                          <Box sx={{ flex: 1 }}>
                            {isStudent ? (
                              <LoanItem loan={loan} isStudent={true} />
                            ) : (
                              <LoanItem
                                loan={loan}
                                isStudent={false}
                                onRenewSelected={onRenewSelected}
                                onReturnEResource={onReturnEResource}
                              />
                            )}
                          </Box>
                        </Box>
                      </React.Fragment>
                    );
                  })}
                </Box>
                {(totalLoans > pageSize || page > pages) && (
                  <Box display="flex" justifyContent="center" mt={2}>
                    <Stack spacing={2}>
                      <Pagination
                        count={pages}
                        color="primary"
                        variant="text"
                        page={page}
                        onChange={handlePageChange}
                        sx={{
                          "& .MuiPagination-ul, .MuiPagination-ul li": {
                            border: "none",
                          },
                        }}
                      />
                    </Stack>
                  </Box>
                )}
              </>
            </Box>
          )}

          <AccountPrintLoansDialog
            open={printDialogOpen}
            onClose={() => {
              closePrintDialog();
              setLoanPrintData([]);
            }}
            loans={loanPrintData}
            isLoading={isPrintLoading}
          />
        </>
      )}
    </>
  );
};
